import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { useTheme } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';
import Seo from '../components/Seo';
import LandingMinimalHeader from '../components/landing/LandingMinimalHeader';
import PrecisionAIIcon from '../components/shared/PrecisionAIIcon';
import AIChatTranscript from '../components/AIChat/AIChatTranscript';
import { createAiChatMarkdownComponents } from '../components/AIChat/aiChatMarkdownComponents';
import { CHAT_SHARE_PATH, loadChatShare } from '../components/AIChat/aiChatShare';
import { DASHBOARD_ROUTES } from '../constants/dashboardRoutes';
import { hasTradePlatformAccess } from '../utils/authAccess';
import './Landing.css';

const PAGE_CONTENT_MAX_WIDTH = 680;

function formatChatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function SharedAIChatPage() {
  const { token: pathToken } = useParams();
  const { hash } = useLocation();
  const { colors } = useTheme();
  const { user } = useContext(AuthContext);
  const markdownComponents = useMemo(() => createAiChatMarkdownComponents(colors), [colors]);
  const hashToken = (hash || '').replace(/^#/, '');
  const token = pathToken || hashToken;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chat, setChat] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await loadChatShare(token);
        if (!cancelled) setChat(data);
      } catch (e) {
        if (!cancelled) {
          setChat(null);
          setError(e.message || 'This shared chat could not be found.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const canOpenApp = Boolean(user?.token) && hasTradePlatformAccess(user);
  const ctaHref = canOpenApp ? DASHBOARD_ROUTES.ai : '/register';
  const ctaLabel = canOpenApp ? 'Open PrecisionAI' : 'Start Trading';
  const chatDate = formatChatDate(chat?.createdAt);

  return (
    <div
      className="shared-ai-chat-page d-flex flex-column"
      data-bs-theme="dark"
      style={{ background: colors.bgPrimary, color: colors.text }}
    >
      <Seo
        title={`${chat?.title || 'Shared chat'} | PrecisionAI`}
        description="A shared PrecisionAI conversation."
        canonicalPath={pathToken ? `${CHAT_SHARE_PATH}/${pathToken}` : CHAT_SHARE_PATH}
        noindex
      />
      <LandingMinimalHeader brandPathname="/home" ctaHref={ctaHref} ctaLabel={ctaLabel} />
      <main
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div className="shared-ai-chat-masthead">
          <div
            style={{
              maxWidth: PAGE_CONTENT_MAX_WIDTH,
              width: '100%',
              margin: '0 auto',
              padding: '18px 16px 16px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
            }}
          >
            <PrecisionAIIcon size={22} active />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 650, color: colors.text, lineHeight: 1.35 }}>
                {chat?.title || 'PrecisionAI chat'}
              </div>
              <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 6 }}>
                {chatDate ? `Read-only · ${chatDate}` : 'Read-only'}
              </div>
            </div>
          </div>
        </div>

        <div className="shared-ai-chat-scroll">
          <div
            style={{
              maxWidth: PAGE_CONTENT_MAX_WIDTH,
              width: '100%',
              margin: '0 auto',
              padding: '20px 16px 28px',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            {loading && (
              <div style={{ padding: '48px 0', textAlign: 'center' }}>
                <Spinner animation="border" size="sm" style={{ color: colors.primary }} />
              </div>
            )}
            {!loading && error && (
              <div style={{ textAlign: 'center', padding: '48px 8px', color: colors.textMuted }}>
                <p style={{ color: colors.text, marginBottom: 16 }}>{error}</p>
                <Link to={ctaHref} style={{ color: colors.primary }}>
                  {ctaLabel}
                </Link>
              </div>
            )}
            {!loading && !error && (
              <>
                <AIChatTranscript
                  messages={chat?.messages || []}
                  markdownComponents={markdownComponents}
                  colors={colors}
                />
                <div
                  style={{
                    marginTop: 8,
                    padding: '14px 12px',
                    borderRadius: 10,
                    backgroundColor: colors.bgSecondary,
                    color: colors.textMuted,
                    fontSize: 13,
                    lineHeight: 1.5,
                    textAlign: 'center',
                  }}
                >
                  {!chat?.live && (
                    <div style={{ marginBottom: 10 }}>
                      This copy was saved when the link was created.
                    </div>
                  )}
                  <Link
                    to={ctaHref}
                    style={{ color: colors.primary, fontWeight: 600, textDecoration: 'none' }}
                  >
                    {ctaLabel}
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
